import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

const ApprovalQueue = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [action, setAction] = useState('');
  const [adminComments, setAdminComments] = useState('');

  useEffect(() => {
    fetchActiveCycle();
  }, []);

  const fetchActiveCycle = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/appraisal/active-cycle');
      const data = await response.json();
      setSelectedCycle(data.cycle_id);
      fetchPendingApprovals(data.cycle_id);
    } catch (error) {
      console.error('Error fetching active cycle:', error);
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async (cycleId) => {
    try {
      const response = await fetch(`http://localhost:8000/api/admin/pending-approvals/${cycleId}`);
      const data = await response.json();
      setPendingApprovals(data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (appraisal) => {
    setSelectedAppraisal(appraisal);
    setAction('approve');
    setShowModal(true);
  };

  const handleReject = (appraisal) => {
    setSelectedAppraisal(appraisal);
    setAction('reject');
    setShowModal(true);
  };

  const submitDecision = async () => {
    if (!selectedAppraisal) return;

    try {
      const endpoint = action === 'approve' 
        ? 'http://localhost:8000/api/admin/approve-compensation'
        : 'http://localhost:8000/api/admin/reject-appraisal';

      const payload = action === 'approve'
        ? {
            emp_code: selectedAppraisal.emp_code,
            cycle_id: selectedCycle,
            final_increment_percent: selectedAppraisal.proposed_hike,
            final_bonus: selectedAppraisal.proposed_bonus,
            admin_comments: adminComments
          }
        : {
            emp_code: selectedAppraisal.emp_code,
            cycle_id: selectedCycle,
            admin_comments: adminComments
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert(`Appraisal ${action}d successfully!`);
        setShowModal(false);
        setAdminComments('');
        fetchPendingApprovals(selectedCycle);
      } else {
        alert(`Failed to ${action} appraisal`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Compensation Approval Queue</h1>

        {pendingApprovals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No pending approvals at this time.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposed Hike</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposed Bonus</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingApprovals.map((appraisal) => (
                  <tr key={appraisal.emp_code} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{appraisal.emp_name}</div>
                        <div className="text-sm text-gray-500">{appraisal.emp_code}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{appraisal.designation}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{appraisal.manager_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{appraisal.avg_rating}/5.0</td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">{appraisal.proposed_hike}%</td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">${appraisal.proposed_bonus}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(appraisal)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Approve"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleReject(appraisal)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Reject"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && selectedAppraisal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {action === 'approve' ? 'Approve' : 'Reject'} Appraisal
            </h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Employee: <span className="font-medium">{selectedAppraisal.emp_name} ({selectedAppraisal.emp_code})</span>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Proposed Hike: <span className="font-medium text-green-600">{selectedAppraisal.proposed_hike}%</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Proposed Bonus: <span className="font-medium text-green-600">${selectedAppraisal.proposed_bonus}</span>
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Comments</label>
              <textarea
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="3"
                placeholder="Enter your comments..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setAdminComments('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitDecision}
                className={`px-4 py-2 rounded-lg text-white ${
                  action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;
